import React, { useState, useRef, useEffect, useCallback } from 'react';

import { useAuth } from '../../context/AuthContext';

import { useLocation, useNavigate } from 'react-router-dom';

import { COMPANY } from '../../config/company';

import api from '../../services/api';

import { useDarkMode } from '../../hooks/useDarkMode';

import { getSocket } from '../../hooks/useSocket';



/* Small pill that opens the Command Palette on click */

function CmdKBadge() {

  const isMac = typeof navigator !== 'undefined' && /Mac|iPod|iPhone|iPad/.test(navigator.platform);

  const openPalette = () => {

    window.dispatchEvent(new KeyboardEvent('keydown', { key: 'k', ctrlKey: !isMac, metaKey: isMac, bubbles: true }));

  };

  return (

    <button

      onClick={openPalette}

      title="Open command palette"

      style={{

        display: 'flex', alignItems: 'center', gap: 5,

        padding: '4px 9px', borderRadius: 7,

        border: '1px solid var(--border)', background: 'var(--surface-2)',

        color: 'var(--text-muted)', cursor: 'pointer',

        fontSize: '0.72rem', fontWeight: 600, letterSpacing: '0.02em',

        transition: 'all 0.15s', whiteSpace: 'nowrap',

      }}

      onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--primary-light)'; e.currentTarget.style.color = 'var(--primary)'; }}

      onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text-muted)'; }}

    >

      <svg width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">

        <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />

      </svg>

      <span>{isMac ? '⌘K' : 'Ctrl K'}</span>

    </button>

  );

}



const pageTitles = {

  '/admin/dashboard': 'Dashboard',

  '/admin/clients': 'Clients',

  '/admin/customers': 'Customers',

  '/admin/catalog': 'Catalog',

  '/admin/inquiries': 'Inquiries',

  '/admin/projects': 'Projects',

  '/admin/orders': 'Orders',

  '/admin/visits': 'Visits',

  '/admin/invoices': 'Invoices',

  '/admin/proforma-invoices': 'Proforma Invoices',

  '/admin/purchase-orders': 'Purchase Orders',

  '/admin/proforma-invoice-orders': 'Proforma Invoice Orders',

  '/admin/tax-invoice-orders': 'Tax Invoice Orders',

  '/admin/quotes': 'Quotes',

  '/admin/analytics': 'Analytics',

  '/admin/hr': 'HR',

  '/admin/users': 'User Management',

  '/admin/audit-logs': 'Audit Logs',

  '/admin/accounting':       'Accounting',

  '/admin/receivables':      'Accounts Receivable',

  '/admin/company-settings': 'Company Settings',

  '/admin/data-export':        'Data Export',

  '/admin/profile':            'My Profile',

  '/admin/attendance':         'Attendance',

  '/admin/my-profile':         'My Employment',

  '/admin/service-requests':   'Service Requests',

  '/admin/coa':                'Certificate of Analysis',

  '/admin/web-inquiries':      'Web Inquiries',

  '/admin/partners':           'Partners',

  '/admin/license-expiry':     'License Expiry',

  '/admin/tva':                'Target vs Achievement',

  '/admin/activities':         'Activities',

  '/admin/tasks':              'Tasks',

  '/admin/territories':        'Territories',

  '/admin/samples':            'Sample Management',

  '/admin/competitors':        'Competitor Tracking',

  '/admin/chem-structure':     'Chemical Structure Editor',

  '/admin/balance-sheet':      'Balance Sheet & P&L',

  '/admin/gst-report':         'GST Report',

  '/admin/webhooks':           'Webhook Integrations',

  '/admin/email-templates':    'Email Templates',

};



const TYPE_ICON = { client: '🏢', customer: '👤', catalog: '🧪', order: '📦', inquiry: '📋', project: '🗂️' };

const TYPE_COLOR = { client: '#6366f1', customer: '#10b981', catalog: '#3b82f6', order: '#f59e0b', inquiry: '#8b5cf6', project: '#ef4444' };



export default function Navbar({ onMenuClick }) {

  const { user, logout, updateUser, isAdmin: ctxIsAdmin, userRoles } = useAuth();

  const location = useLocation();

  const navigate = useNavigate();

  const [dropOpen, setDropOpen] = useState(false);

  const [bellOpen, setBellOpen] = useState(false);

  const [switchLoginOpen, setSwitchLoginOpen] = useState(false);

  const [switchAccounts, setSwitchAccounts] = useState([]);

  const [switchCredentials, setSwitchCredentials] = useState({ identifier: '', password: '' });

  const [switchLoading, setSwitchLoading] = useState(false);

  const [switchError, setSwitchError] = useState('');

  const [firstTimeSwitchOpen, setFirstTimeSwitchOpen] = useState(false);

  const [targetAccount, setTargetAccount] = useState(null);

  const [confirmPassword, setConfirmPassword] = useState('');

  const [notifications, setNotifications] = useState([]);

  const [searchQ, setSearchQ] = useState('');

  const [searchResults, setSearchResults] = useState([]);

  const [searchOpen, setSearchOpen] = useState(false);

  const [searchLoading, setSearchLoading] = useState(false);

  const searchRef = useRef(null);

  const searchTimer = useRef(null);

  const dropRef = useRef(null);

  const bellRef = useRef(null);

  
  const [dark, setDark] = useDarkMode();

  const title = pageTitles[location.pathname] || `${COMPANY.shortName} CRM`;



  const fetchNotifications = useCallback(async () => {

    try {

      const { data: res } = await api.get('/dashboard/notifications');

      setNotifications(res.data || []);

    } catch {}

  }, []);



  // Load saved accounts from session storage

  const loadSavedAccounts = useCallback(() => {

    try {

      const saved = sessionStorage.getItem('switchAccounts');

      if (saved) {

        const accounts = JSON.parse(saved);

        setSwitchAccounts(accounts);

      }

    } catch (error) {

      console.error('Failed to load saved accounts:', error);

    }

  }, []);



  // Save account to session storage

  const saveAccountToSession = useCallback((userData, token) => {

    try {

      const existingAccounts = JSON.parse(sessionStorage.getItem('switchAccounts') || '[]');

      

      // Remove if already exists

      const filtered = existingAccounts.filter(acc => acc.id !== userData.id);

      

      // Add new account

      const updatedAccounts = [...filtered, {

        ...userData,

        token: token,

        savedAt: new Date().toISOString()

      }];

      

      // Keep only last 5 accounts

      const limited = updatedAccounts.slice(-5);

      

      sessionStorage.setItem('switchAccounts', JSON.stringify(limited));

      setSwitchAccounts(limited);

    } catch (error) {

      console.error('Failed to save account:', error);

    }

  }, []);



  // Remove account from saved list

  const handleRemoveAccount = useCallback((accountId, e) => {

    e.stopPropagation(); // Prevent switching

    

    const updated = switchAccounts.filter(acc => acc.id !== accountId);

    sessionStorage.setItem('switchAccounts', JSON.stringify(updated));

    setSwitchAccounts(updated);

  }, [switchAccounts]);



  // Switch to saved account (with password protection for first time)
  const handleSwitchToSavedAccount = useCallback(async (savedAccount, skipPassword = false) => {
    // First-time password gate: admin must confirm their password once per session
    if (!skipPassword && ctxIsAdmin) {
      const hasSwitchedBefore = sessionStorage.getItem('hasSwitchedAccount');
      if (!hasSwitchedBefore) {
        setTargetAccount(savedAccount);
        setFirstTimeSwitchOpen(true);
        setSwitchError('');
        return;
      }
    }

    setSwitchLoading(true);

    try {
      let token;

      if (ctxIsAdmin) {
        // Admin: use the backend switch-account endpoint to get a fresh access token
        // AND have the server set a new refresh cookie for the target user.
        // This prevents the refresh mechanism from silently reverting to admin.
        const { data } = await api.post('/auth/switch-account', { targetUserId: savedAccount.id });
        token = data.token;
      } else {
        // Non-admin (e.g. switching back to an admin who previously saved their session):
        // reuse the saved token. If it is expired the page will redirect to login.
        token = savedAccount.token;
      }

      localStorage.setItem('token', token);
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      updateUser(savedAccount);

      setDropOpen(false);
      navigate('/admin/dashboard');
      window.location.reload();

    } catch (error) {
      console.error('Failed to switch account:', error);

      const updated = switchAccounts.filter(acc => acc.id !== savedAccount.id);
      sessionStorage.setItem('switchAccounts', JSON.stringify(updated));
      setSwitchAccounts(updated);

      alert('Account session expired. Please login again.');
    } finally {
      setSwitchLoading(false);
    }

  }, [switchAccounts, updateUser, navigate, user?.role]);



  // Handle first-time password confirmation
  const handleFirstTimeSwitch = useCallback(async () => {
    if (!confirmPassword) {
      setSwitchError('Password is required');
      return;
    }

    setSwitchLoading(true);
    setSwitchError('');

    try {
      // Verify admin password before switching
      const { data } = await api.post('/auth/verify-password', {
        password: confirmPassword
      });

      if (data.valid) {
        // Mark that switching has occurred
        sessionStorage.setItem('hasSwitchedAccount', 'true');
        
        // Proceed with actual switch
        await handleSwitchToSavedAccount(targetAccount, true);
        
        // Close password modal
        setFirstTimeSwitchOpen(false);
        setTargetAccount(null);
        setConfirmPassword('');
      } else {
        setSwitchError('Invalid password');
      }
    } catch (error) {
      setSwitchError('Password verification failed');
    } finally {
      setSwitchLoading(false);
    }
  }, [confirmPassword, targetAccount, handleSwitchToSavedAccount, user?.email, user?.username]);



  // Login and save new account

  const handleSwitchAccountLogin = async () => {

    setSwitchError('');

    setSwitchLoading(true);

    

    try {

      const { data } = await api.post('/auth/login', {

        identifier: switchCredentials.identifier,

        password: switchCredentials.password

      });

      

      // Save current account before switching

      if (user && localStorage.getItem('token')) {

        saveAccountToSession(user, localStorage.getItem('token'));

      }

      

      // Update token and user context

      localStorage.setItem('token', data.token);

      api.defaults.headers.common['Authorization'] = `Bearer ${data.token}`;

      updateUser(data.user);

      

      // Save new account to session

      saveAccountToSession(data.user, data.token);

      

      // Close modals and reset

      setSwitchLoginOpen(false);

      setDropOpen(false);

      setSwitchCredentials({ identifier: '', password: '' });

      

      // Navigate to dashboard to refresh the view

      navigate('/admin/dashboard');

      window.location.reload(); // Force reload to update all components

      

    } catch (error) {

      setSwitchError(error.response?.data?.message || 'Login failed. Please check your credentials.');

    } finally {

      setSwitchLoading(false);

    }

  };



  useEffect(() => {

    fetchNotifications();

    const interval = setInterval(fetchNotifications, 60000);

    return () => clearInterval(interval);

  }, [fetchNotifications]);



  // Load saved accounts on component mount

  useEffect(() => {

    loadSavedAccounts();

  }, [loadSavedAccounts]);



  // Save current account to session when user logs in

  useEffect(() => {

    if (user && localStorage.getItem('token')) {

      saveAccountToSession(user, localStorage.getItem('token'));

    }

  }, [user, saveAccountToSession]);



  

  // Socket.io: join user room when user is available

  useEffect(() => {

    if (user?.id) {

      getSocket().emit('join', user.id);

    }

  }, [user?.id]);



  // Socket.io: listen for real-time events

  useEffect(() => {

    const socket = getSocket();



    const onOrderStatus = (data) => {

      setNotifications(prev => [{

        type: 'order_update',

        message: `Order ${data.orderNumber} → ${data.status}`,

        link: '/admin/orders',

      }, ...prev.slice(0, 9)]);

    };



    const onInquiry = (data) => {

      setNotifications(prev => [{

        type: 'web_inquiry',

        message: `New inquiry from ${data.clientName || 'client'}`,

        link: '/admin/inquiries',

      }, ...prev.slice(0, 9)]);

    };



    socket.on('order:status_changed', onOrderStatus);

    socket.on('inquiry:created', onInquiry);



    return () => {

      socket.off('order:status_changed', onOrderStatus);

      socket.off('inquiry:created', onInquiry);

    };

  }, []);



  const handleSearch = useCallback((val) => {

    setSearchQ(val);

    clearTimeout(searchTimer.current);

    if (!val || val.length < 2) { setSearchResults([]); setSearchOpen(false); return; }

    searchTimer.current = setTimeout(async () => {

      setSearchLoading(true);

      try {

        const { data: res } = await api.get('/search', { params: { q: val } });

        setSearchResults(res.data || []);

        setSearchOpen(true);

      } catch {}

      setSearchLoading(false);

    }, 300);

  }, []);



  useEffect(() => {

    const handler = (e) => {

      if (dropRef.current && !dropRef.current.contains(e.target)) setDropOpen(false);

      if (bellRef.current && !bellRef.current.contains(e.target)) setBellOpen(false);

      if (searchRef.current && !searchRef.current.contains(e.target)) setSearchOpen(false);

    };

    document.addEventListener('mousedown', handler);

    return () => document.removeEventListener('mousedown', handler);

  }, []);


  return (
    <>

    {/* First-Time Switch Password Confirmation Modal */}
    {firstTimeSwitchOpen && (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0, 0, 0, 0.5)', display: 'flex', alignItems: 'center',
      justifyContent: 'center', zIndex: 1000
    }}>
      <div style={{
        background: '#fff', borderRadius: 12, padding: '24px',
        width: '90%', maxWidth: '400px', boxShadow: 'var(--shadow-lg)'
      }}>
        <div style={{ marginBottom: '20px' }}>
          <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 700, color: 'var(--text-primary)' }}>
            Security Verification
          </h2>
          <p style={{ margin: '8px 0 0 0', fontSize: '0.875rem', color: 'var(--text-muted)' }}>
            For security, please enter your password to switch to <strong>{targetAccount?.name}</strong>
          </p>
        </div>

        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', marginBottom: '6px', fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-primary)' }}>
            Your Password
          </label>
          <input
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder="Enter your admin password"
            style={{
              width: '100%', padding: '10px 12px', border: '1px solid var(--border)',
              borderRadius: 8, fontSize: '0.875rem', outline: 'none',
              boxSizing: 'border-box'
            }}
            onKeyPress={(e) => e.key === 'Enter' && handleFirstTimeSwitch()}
            autoFocus
          />
        </div>

        {switchError && (
          <div style={{
            marginBottom: '16px', padding: '8px 12px', background: '#fef2f2',
            border: '1px solid #fecaca', borderRadius: 6, fontSize: '0.875rem',
            color: '#dc2626'
          }}>
            {switchError}
          </div>
        )}

        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          <button
            onClick={() => {
              setFirstTimeSwitchOpen(false);
              setTargetAccount(null);
              setConfirmPassword('');
              setSwitchError('');
            }}
            disabled={switchLoading}
            style={{
              padding: '8px 16px', border: '1px solid var(--border)', borderRadius: 8,
              background: '#fff', fontSize: '0.875rem', fontWeight: 500,
              cursor: switchLoading ? 'not-allowed' : 'pointer', color: 'var(--text-primary)'
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleFirstTimeSwitch}
            disabled={switchLoading || !confirmPassword}
            style={{
              padding: '8px 16px', border: 'none', borderRadius: 8,
              background: switchLoading ? 'var(--border)' : 'var(--primary)',
              color: switchLoading ? 'var(--text-muted)' : '#fff',
              fontSize: '0.875rem', fontWeight: 500,
              cursor: switchLoading ? 'not-allowed' : 'pointer'
            }}
          >
            {switchLoading ? 'Verifying...' : 'Switch Account'}
          </button>
        </div>
      </div>
    </div>
    )}

    <header className="admin-navbar" style={{
      position: 'fixed', top: 0, left: 'var(--sidebar-width)', right: 0, height: 'var(--navbar-height)',
      background: '#fff', borderBottom: '1px solid var(--border)', display: 'flex',
      alignItems: 'center', justifyContent: 'space-between', padding: '0 24px', zIndex: 30,

      boxShadow: '0 1px 3px rgba(0,0,0,0.04)'

    }}>

      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>

        <button onClick={onMenuClick}

          style={{ display: 'none', background: 'none', border: 'none', cursor: 'pointer', padding: 5, borderRadius: 6, color: 'var(--text-secondary)' }}

          className="mobile-menu-btn">

          <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">

            <line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/>

          </svg>

        </button>

        <h1 style={{ fontSize: '1.1rem', fontWeight: 700, color: 'var(--text-primary)' }}>{title}</h1>

      </div>



      {/* Global search */}

      <div ref={searchRef} style={{ position: 'relative', flex: '0 1 280px' }}>

        <div style={{ position: 'relative' }}>

          <svg width="14" height="14" fill="none" stroke="var(--text-muted)" strokeWidth="2" viewBox="0 0 24 24"

            style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>

            <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>

          </svg>

          <input

            value={searchQ}

            onChange={e => handleSearch(e.target.value)}

            onFocus={() => searchResults.length > 0 && setSearchOpen(true)}

            placeholder="Search clients, orders, catalog…"

            style={{ width: '100%', paddingLeft: 32, paddingRight: 10, height: 34, borderRadius: 8, border: '1px solid var(--border)', fontSize: '0.82rem', background: 'var(--surface-2)', color: 'var(--text-primary)', outline: 'none', boxSizing: 'border-box' }}

          />

          {searchLoading && (

            <span className="spinner" style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', width: 12, height: 12, borderWidth: 2 }} />

          )}

        </div>

        {searchOpen && searchResults.length > 0 && (

          <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, background: '#fff', border: '1px solid var(--border)', borderRadius: 10, boxShadow: 'var(--shadow-md)', zIndex: 200, maxHeight: 320, overflowY: 'auto' }}>

            {searchResults.map((r, i) => (

              <button key={i} onClick={() => { navigate(r.link); setSearchOpen(false); setSearchQ(''); setSearchResults([]); }}

                style={{ width: '100%', padding: '8px 12px', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10, borderBottom: i < searchResults.length - 1 ? '1px solid #f1f5f9' : 'none' }}

                onMouseEnter={e => e.currentTarget.style.background = 'var(--surface-2)'}

                onMouseLeave={e => e.currentTarget.style.background = 'none'}

              >

                <span style={{ fontSize: '1rem' }}>{TYPE_ICON[r.type] || '🔍'}</span>

                <div style={{ flex: 1, minWidth: 0 }}>

                  <div style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.title}</div>

                  {r.subtitle && <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>{r.subtitle}</div>}

                </div>

                <span style={{ fontSize: '0.68rem', padding: '1px 6px', borderRadius: 8, background: TYPE_COLOR[r.type] + '18', color: TYPE_COLOR[r.type], fontWeight: 600, textTransform: 'capitalize', flexShrink: 0 }}>{r.type}</span>

              </button>

            ))}

          </div>

        )}

        {searchOpen && searchQ.length >= 2 && searchResults.length === 0 && !searchLoading && (

          <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, background: '#fff', border: '1px solid var(--border)', borderRadius: 10, boxShadow: 'var(--shadow-md)', zIndex: 200, padding: '14px 12px', fontSize: '0.82rem', color: 'var(--text-muted)', textAlign: 'center' }}>

            No results found

          </div>

        )}

      </div>



      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>

        {/* Command Palette shortcut hint */}

        <CmdKBadge />

        {/* Dark mode toggle */}

        <button onClick={() => setDark(d => !d)}

          title={dark ? 'Switch to light mode' : 'Switch to dark mode'}

          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 7, borderRadius: 8, color: 'var(--text-muted)', display: 'flex', transition: 'color 0.15s' }}>

          {dark ? (

            <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">

              <circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/>

              <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>

              <line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/>

              <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>

            </svg>

          ) : (

            <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">

              <path d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z"/>

            </svg>

          )}

        </button>

        <div ref={bellRef} style={{ position: 'relative' }}>

          <button onClick={() => setBellOpen(!bellOpen)}

            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 7, borderRadius: 8, color: 'var(--text-muted)', position: 'relative', display: 'flex' }}>

            <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">

              <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 01-3.46 0"/>

            </svg>

            {notifications.length > 0 && (

              <span style={{ position: 'absolute', top: 5, right: 5, width: 7, height: 7, background: 'var(--error)', borderRadius: '50%', border: '1.5px solid #fff' }} />

            )}

          </button>

          {bellOpen && (

            <div style={{

              position: 'absolute', top: 'calc(100% + 6px)', right: 0, width: 300,

              background: '#fff', borderRadius: 10, border: '1px solid var(--border)',

              boxShadow: 'var(--shadow-md)', overflow: 'hidden', zIndex: 100,

            }}>

              <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--border)', fontWeight: 700, fontSize: '0.875rem' }}>

                Notifications {notifications.length > 0 && <span style={{ background: 'var(--error)', color: '#fff', borderRadius: 10, padding: '1px 8px', fontSize: '0.72rem', marginLeft: 6 }}>{notifications.length}</span>}

              </div>

              {notifications.length === 0 ? (

                <div style={{ padding: '20px 14px', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.82rem' }}>All caught up!</div>

              ) : (

                notifications.map((n, i) => {

                  const icons = { leave: '📋', web_inquiry: '🌐', milestone: '⚠️', overdue: '💰', quote_expiry: '📅', stock_alert: '📦', activity: '✅', license: '🪪' };

                  const colors = { leave: '#f59e0b', web_inquiry: '#6366f1', milestone: '#ef4444', overdue: '#dc2626', quote_expiry: '#f59e0b', stock_alert: '#ef4444', activity: '#ef4444', license: '#f59e0b' };

                  return (

                    <button key={i} onClick={() => { navigate(n.link); setBellOpen(false); }}

                      style={{ width: '100%', padding: '10px 14px', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', borderBottom: i < notifications.length - 1 ? '1px solid #f1f5f9' : 'none', display: 'flex', alignItems: 'center', gap: 10, transition: 'background 0.1s' }}

                      onMouseEnter={e => e.currentTarget.style.background = 'var(--surface-2)'}

                      onMouseLeave={e => e.currentTarget.style.background = 'none'}

                    >

                      <span style={{ fontSize: '1.1rem' }}>{icons[n.type] || '🔔'}</span>

                      <span style={{ fontSize: '0.82rem', color: colors[n.type] || 'var(--text-primary)', fontWeight: 600, flex: 1 }}>{n.message}</span>

                      <svg width="12" height="12" fill="none" stroke="#94a3b8" strokeWidth="2" viewBox="0 0 24 24"><polyline points="9 18 15 12 9 6"/></svg>

                    </button>

                  );

                })

              )}

            </div>

          )}

        </div>



        <div ref={dropRef} style={{ position: 'relative' }}>

          <button onClick={() => setDropOpen(!dropOpen)}

            style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '5px 10px', borderRadius: 8, border: '1px solid var(--border)', background: dropOpen ? 'var(--surface-2)' : '#fff', cursor: 'pointer', transition: 'all 0.15s' }}>

            <div style={{ width: 30, height: 30, borderRadius: '50%', background: 'var(--primary-lighter)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '0.85rem', color: 'var(--primary)' }}>

              {user?.name?.charAt(0).toUpperCase()}

            </div>

            <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">

              <polyline points="6 9 12 15 18 9"/>

            </svg>

          </button>



          {dropOpen && (

            <div style={{

              position: 'absolute', top: 'calc(100% + 6px)', right: 0, width: 200,

              background: '#fff', borderRadius: 10, border: '1px solid var(--border)',

              boxShadow: 'var(--shadow-md)', overflow: 'hidden', zIndex: 100,

              animation: 'slideUp 0.15s ease'

            }}>

              <div style={{ padding: '12px 14px', borderBottom: '1px solid var(--border)' }}>

                <div style={{ fontWeight: 600, fontSize: '0.875rem' }}>{user?.name}</div>

                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{user?.email}</div>

                <span className="badge badge-primary" style={{ marginTop: 5, textTransform: 'capitalize' }}>{user?.role}</span>

              </div>

              

              {/* Switch Account Section - Only for Admin Users */}

              {ctxIsAdmin && (

              <div style={{ borderBottom: '1px solid var(--border)' }}>

                <div style={{ padding: '8px 14px', fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase' }}>

                  Switch Account

                </div>

                

                {/* Show saved accounts - always show admin accounts and other accounts except current */}

                {switchAccounts.filter(acc => acc.role === 'admin' || acc.id !== user?.id).slice(0, 3).map((account) => {

                  const isCurrentAccount = account.id === user?.id;

                  return (

                  <div key={account.id} style={{ position: 'relative' }}>

                    <button

                      onClick={() => !isCurrentAccount && handleSwitchToSavedAccount(account)}

                      disabled={switchLoading || isCurrentAccount}

                      style={{ 

                        width: '100%', padding: '10px 14px', textAlign: 'left', background: isCurrentAccount ? 'var(--primary-lighter)' : 'none', border: 'none', 

                        cursor: switchLoading || isCurrentAccount ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: 10,

                        opacity: switchLoading ? 0.6 : 1

                      }}

                      onMouseEnter={e => !switchLoading && (e.currentTarget.style.background = 'var(--surface-2)')}

                      onMouseLeave={e => e.currentTarget.style.background = 'none'}

                    >

                      <div style={{ 

                        width: 32, height: 32, borderRadius: '50%', background: 'var(--primary-lighter)', 

                        display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, 

                        fontSize: '0.8rem', color: 'var(--primary)' 

                      }}>

                        {account.name.charAt(0).toUpperCase()}

                      </div>

                      <div style={{ flex: 1, minWidth: 0 }}>

                        <div style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: '0.875rem' }}>

                          {account.name}

                        </div>

                        <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>

                          {account.email}

                        </div>

                      </div>

                      <span style={{ 

                        fontSize: '0.68rem', padding: '2px 6px', borderRadius: 6, 

                        background: isCurrentAccount ? 'var(--primary)' : 'var(--primary-lighter)', 

                        color: isCurrentAccount ? 'white' : 'var(--primary)', fontWeight: 600 

                      }}>

                        {isCurrentAccount ? 'Current' : account.role}

                      </span>

                    </button>

                    <button

                      onClick={(e) => handleRemoveAccount(account.id, e)}

                      style={{

                        position: 'absolute', top: '10px', right: '10px',

                        width: 20, height: 20, borderRadius: '50%', background: 'rgba(0,0,0,0.1)',

                        border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center',

                        justifyContent: 'center', opacity: 0.7

                      }}

                      onMouseEnter={e => e.currentTarget.style.opacity = '1'}

                      onMouseLeave={e => e.currentTarget.style.opacity = '0.7'}

                      title="Remove account"

                    >

                      <svg width="12" height="12" fill="none" stroke="#666" strokeWidth="2" viewBox="0 0 24 24">

                        <line x1="18" y1="6" x2="6" y2="18"/>

                        <line x1="6" y1="6" x2="18" y2="18"/>

                      </svg>

                    </button>

                  </div>

                  );

                })}

                

                {/* Add Account Button */}

                <button 

                  onClick={() => setSwitchLoginOpen(true)}

                  style={{ 

                    width: '100%', padding: '10px 14px', textAlign: 'left', background: 'none', border: 'none', 

                    cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.875rem', 

                    color: 'var(--primary)', fontWeight: 500

                  }}

                  onMouseEnter={e => e.currentTarget.style.background = 'var(--surface-2)'}

                  onMouseLeave={e => e.currentTarget.style.background = 'none'}

                >

                  <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">

                    <circle cx="12" cy="12" r="10"/>

                    <line x1="12" y1="8" x2="12" y2="16"/>

                    <line x1="8" y1="12" x2="16" y2="12"/>

                  </svg>

                  Add Account

                </button>

              </div>

              )}

              

              <button onClick={logout}

                style={{ width: '100%', padding: '11px 14px', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.875rem', color: 'var(--error)', fontWeight: 500 }}

                onMouseEnter={e => e.currentTarget.style.background = 'var(--error-bg)'}

                onMouseLeave={e => e.currentTarget.style.background = 'none'}

              >

                <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">

                  <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/>

                </svg>

                Sign Out

              </button>

            </div>

          )}

        </div>

      </div>

    </header>



    {/* Switch Account Login Modal */}

    {switchLoginOpen && (

    <div style={{

      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,

      background: 'rgba(0, 0, 0, 0.5)', display: 'flex', alignItems: 'center',

      justifyContent: 'center', zIndex: 1000

    }}>

      <div style={{

        background: '#fff', borderRadius: 12, padding: '24px',

        width: '90%', maxWidth: '400px', boxShadow: 'var(--shadow-lg)'

      }}>

        <div style={{ marginBottom: '20px' }}>

          <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 700, color: 'var(--text-primary)' }}>

            Switch Account

          </h2>

          <p style={{ margin: '8px 0 0 0', fontSize: '0.875rem', color: 'var(--text-muted)' }}>

            Enter credentials for the account you want to switch to

          </p>

        </div>



        <div style={{ marginBottom: '16px' }}>

          <label style={{ display: 'block', marginBottom: '6px', fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-primary)' }}>

            Email, Username, or Phone

          </label>

          <input

            type="text"

            value={switchCredentials.identifier}

            onChange={(e) => setSwitchCredentials(prev => ({ ...prev, identifier: e.target.value }))}

            placeholder="Enter email, username, or phone"

            style={{

              width: '100%', padding: '10px 12px', border: '1px solid var(--border)',

              borderRadius: 8, fontSize: '0.875rem', outline: 'none',

              boxSizing: 'border-box'

            }}

            onKeyPress={(e) => e.key === 'Enter' && handleSwitchAccountLogin()}

          />

        </div>



        <div style={{ marginBottom: '16px' }}>

          <label style={{ display: 'block', marginBottom: '6px', fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-primary)' }}>

            Password

          </label>

          <input

            type="password"

            value={switchCredentials.password}

            onChange={(e) => setSwitchCredentials(prev => ({ ...prev, password: e.target.value }))}

            placeholder="Enter password"

            style={{

              width: '100%', padding: '10px 12px', border: '1px solid var(--border)',

              borderRadius: 8, fontSize: '0.875rem', outline: 'none',

              boxSizing: 'border-box'

            }}

            onKeyPress={(e) => e.key === 'Enter' && handleSwitchAccountLogin()}

          />

        </div>



        {switchError && (

          <div style={{

            marginBottom: '16px', padding: '8px 12px', background: '#fef2f2',

            border: '1px solid #fecaca', borderRadius: 6, fontSize: '0.875rem',

            color: '#dc2626'

          }}>

            {switchError}

          </div>

        )}



        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>

          <button

            onClick={() => {

              setSwitchLoginOpen(false);

              setSwitchCredentials({ identifier: '', password: '' });

              setSwitchError('');

            }}

            disabled={switchLoading}

            style={{

              padding: '8px 16px', border: '1px solid var(--border)', borderRadius: 8,

              background: '#fff', fontSize: '0.875rem', fontWeight: 500,

              cursor: switchLoading ? 'not-allowed' : 'pointer', color: 'var(--text-primary)'

            }}

          >

            Cancel

          </button>

          <button

            onClick={handleSwitchAccountLogin}

            disabled={switchLoading || !switchCredentials.identifier || !switchCredentials.password}

            style={{

              padding: '8px 16px', border: 'none', borderRadius: 8,

              background: switchLoading ? 'var(--border)' : 'var(--primary)',

              color: switchLoading ? 'var(--text-muted)' : '#fff',

              fontSize: '0.875rem', fontWeight: 500,

              cursor: switchLoading ? 'not-allowed' : 'pointer'

            }}

          >

            {switchLoading ? 'Switching...' : 'Switch Account'}

          </button>

        </div>

      </div>

    </div>

    )}

    </>

  );

}

